namespace $ {

	export type $shm_hitalama_benchmarks_goods_config = {
		storage: 'single' | 'area' | 'shard16' | 'shard64' | 'shard256'
		properties: 4 | 10
		packing: 'granular' | 'packed'
		access?: 'private' | 'public_read'
		allocation?: 'derived' | 'production'
	}

	/** Isolated fixture commands. Never changes production allocation defaults. */
	export class $shm_hitalama_benchmarks_goods extends $mol_object {
		private prepared_map?: $shm_hitalama_map
		private prepared_widget?: $shm_hitalama_map_widget
		private prepared_dataset?: $shm_hitalama_map_widget_entity_table2_dataset
		private prepared_tags?: $shm_hitalama_map_widget
		private prepared_tags_dataset?: $shm_hitalama_map_widget_entity_table2_dataset
		private readonly prepared_lands = new Map< number, $giper_baza_land >()
		private readonly prepared_records = new Map< number, $shm_hitalama_map_widget_entity_table2_record >()
		private configuration: $shm_hitalama_benchmarks_goods_config = { storage: 'shard256', properties: 4, packing: 'granular' }

		@ $mol_action
		attach( config: $shm_hitalama_benchmarks_goods_config, fixture: { map: string, widget: string, dataset: string } ) {
			if( this.prepared_map ) return this
			this.config( config )
			this.prepared_map = this.$.$giper_baza_glob.Pawn( new $giper_baza_link( fixture.map ), $shm_hitalama_map )
			this.prepared_widget = this.$.$giper_baza_glob.Pawn( new $giper_baza_link( fixture.widget ), $shm_hitalama_map_widget )
			this.prepared_dataset = this.$.$giper_baza_glob.Pawn( new $giper_baza_link( fixture.dataset ), $shm_hitalama_map_widget_entity_table2_dataset )
			this.prepared_tags = this.prepared_map.Widgets_list()?.remote_list().find( widget=>widget.title() === 'Tags' )
			return this
		}

		@ $mol_mem
		config( next?: $shm_hitalama_benchmarks_goods_config ) {
			if( next ) this.configuration = next
			return this.configuration
		}

		@ $mol_mem
		map() {
			$mol_wire_solid()
			if( this.prepared_map ) return this.$.$giper_baza_glob.Pawn( this.prepared_map.link(), $shm_hitalama_map )
			this.$.$giper_baza_auth.current().pass()
			this.prepared_map = this.$.$shm_hitalama_map.make_new( 'Goods benchmark', this.config().access ?? 'private' )
			return this.$.$giper_baza_glob.Pawn( this.prepared_map.link(), $shm_hitalama_map )
		}

		@ $mol_mem
		widget() {
			$mol_wire_solid()
			this.prepared_widget ??= this.map().widget_add( 'table2', [ 40, 40 ], 1200, 680, 'Goods' )
			if( !this.prepared_widget ) throw new Error( 'Could not create goods table fixture' )
			return this.$.$giper_baza_glob.Pawn( this.prepared_widget.link(), $shm_hitalama_map_widget )
		}

		@ $mol_mem
		dataset() {
			$mol_wire_solid()
			this.prepared_dataset ??= this.widget().dataset()
			return this.$.$giper_baza_glob.Pawn( this.prepared_dataset.link(), $shm_hitalama_map_widget_entity_table2_dataset )
		}

		@ $mol_action
		open() {
			const map = this.map().link().str
			this.$.$mol_state_arg.value( 'section', 'maps' )
			this.$.$mol_state_arg.value( 'map', map )
			this.$.$shm_hitalama_app.Root( 0 ).Book().spread( 'maps' )
			this.$.$shm_hitalama_app.Root( 0 ).Maps().spread( map )
			return map
		}

		@ $mol_mem
		tags_widget() {
			$mol_wire_solid()
			this.prepared_tags ??= this.map().widget_add( 'table2', [ 1300, 40 ], 600, 400, 'Tags' )
			if( !this.prepared_tags ) throw new Error( 'Could not create tag table fixture' )
			return this.$.$giper_baza_glob.Pawn( this.prepared_tags.link(), $shm_hitalama_map_widget )
		}

		@ $mol_mem
		tags_dataset() {
			$mol_wire_solid()
			this.prepared_tags_dataset ??= this.tags_widget().dataset()
			return this.$.$giper_baza_glob.Pawn( this.prepared_tags_dataset.link(), $shm_hitalama_map_widget_entity_table2_dataset )
		}

		@ $mol_action
		prepare( publish = true ) {
			const map = this.map()
			if( publish ) {
			const profile = this.$.$shm_hitalama_profile.current()!
			const maps = profile.Maps_list( null )!
			if( !maps.items().some( link=>link.str === map.link().str ) ) maps.add( map.link() )
			profile.map_summary( map.link().str, map.summary() )
			}
			const dataset = this.dataset()
			const full = this.config().properties === 10
			const schema = 'Product Name #string\nID #string\nColor #select\nStatus #select'
				+ ( full ? '\nChecked #number\nURI #string\nQuantity #number\nSupply Time #date\nDescription #textarea\nTags #list_link' : '' )
			if( dataset.fields().length !== this.config().properties ) dataset.text( schema )
			dataset.field( 'Color' )!.options([ 'red', 'green', 'blue' ])
			dataset.field( 'Status' )!.options([ 'minor', 'major', 'critical' ])
			if( full ) {
				const tags = this.tags_dataset()
				if( !tags.fields().length ) tags.text( 'Name #string' )
				$mol_wire_sync( this ).tags_prepare()
				dataset.field( 'Tags' )!.target_dataset( tags )
				dataset.field( 'Tags' )!.display_field( tags.fields()[0] )
			}
			if( !dataset.record_index_active() ) dataset.record_index_activate()
			this.widget().table().rows_limit( 10 )
			return { map: this.map().link().str, widget: this.widget().link().str, dataset: dataset.link().str }
		}

		private readonly tag_objects = Array.from( { length: 20 }, ( _, index )=> ({ Name: [ 'Office', 'Home', 'Travel', 'Studio', 'Gaming', 'Portable', 'Wireless', 'Compact', 'Premium', 'Classic', 'New', 'Popular', 'Eco', 'Gift', 'Work', 'Creative', 'Audio', 'Video', 'Network', 'Accessories' ][index] }) )
		private readonly prepared_tag_records = new Map< number, $shm_hitalama_map_widget_entity_table2_record >()
		private readonly initialized_tags = new Set< number >()

		@ $mol_mem_key
		tag_metadata( index: number ) { return { dataset: this.tags_dataset() } }

		@ $mol_mem_key
		tag_record( index: number ) {
			$mol_wire_solid()
			const existing = this.prepared_tag_records.get( index )
			if( existing ) return this.$.$giper_baza_glob.Pawn( existing.link(), $shm_hitalama_map_widget_entity_table2_record )
			const land = this.tags_dataset().land()
			const record = land.Pawn( $shm_hitalama_map_widget_entity_table2_record ).Head( $mol_wire_sync( land ).self_make() )
			this.prepared_tag_records.set( index, record )
			return record
		}

		@ $mol_action
		tag_initialize( index: number ) {
			const record = this.tag_record( index )
			if( !this.initialized_tags.has( index ) ) {
				const dataset = this.tags_dataset()
				$mol_wire_sync( record ).initial_write( dataset.fields(), this.tag_objects[index], this.tag_metadata( index ) )
				this.initialized_tags.add( index )
			}
			return record
		}

		@ $mol_mem
		tag_batch() { return this.tag_objects.map( ( _, index )=> $mol_wire_sync( this ).tag_initialize( index ) ) }

		@ $mol_action
		tags_prepare() {
			const tags = this.tags_dataset()
			if( tags.record_count() === 20 ) return tags.records()
			const records = this.tag_batch()
			if( !tags.record_index_active() ) $mol_wire_sync( tags ).record_index_activate()
			$mol_wire_sync( tags ).record_index_append( records )
			return records
		}

		@ $mol_mem_key
		land( index: number ) {
			$mol_wire_solid()
			const dataset = this.dataset()
			const mode = this.config().storage
			if( mode === 'single' ) return dataset.land()
			const size = mode === 'area' ? 1 : mode === 'shard16' ? 16 : mode === 'shard64' ? 64 : 256
			const shard = Math.floor( index / size )
			const existing = this.prepared_lands.get( shard )
			if( existing ) return this.$.$giper_baza_glob.Land( existing.link() ).sync()
			const land = this.config().allocation === 'production'
				? $mol_wire_sync( dataset ).record_shard( shard )
				: $mol_wire_sync( dataset.land() ).area_make( shard + 1 )
			this.prepared_lands.set( shard, land )
			return land
		}

		@ $mol_mem_key
		record( index: number ) {
			$mol_wire_solid()
			const existing = this.prepared_records.get( index )
			if( existing ) return this.$.$giper_baza_glob.Pawn( existing.link(), $shm_hitalama_map_widget_entity_table2_record )
			const land = this.land( index )
			const record = land.Pawn( $shm_hitalama_map_widget_entity_table2_record ).Head( $mol_wire_sync( land ).self_make() )
			this.prepared_records.set( index, record )
			return record
		}

		@ $mol_mem_key
		object( index: number ) {
			const name = $shm_hitalama_benchmarks_goods.product_name( index )
			const base = { 'Product Name': name, ID: String( index ).padStart( 5, '0' ), Color: [ 'red', 'green', 'blue' ][index % 3], Status: [ 'minor', 'major', 'critical' ][index % 3] }
			if( this.config().properties === 4 ) return base
			const tags = this.tags_dataset().records()
			return { ...base, Checked: 0, URI: `https://xkcd.com/${ index + 1 }/`, Quantity: index % 100, 'Supply Time': '2026-10-05', Description: `${ name } — sample product for the local CRDT benchmark.`, Tags: Array.from( { length: 5 }, ( _, offset )=> tags[(index + offset * 3) % tags.length].link().str ) }
		}

		private readonly completed = new Set< number >()

		@ $mol_mem_key
		metadata( index: number ) { return { dataset: this.dataset(), stamp: 1791158400000 + index, user: '' } }

		@ $mol_action
		initialize_record( index: number ) {
			const record = this.record( index )
			if( this.completed.has( index ) ) return record
			const fields = this.dataset().fields()
			const object = this.object( index ) as Record< string, any >
			if( this.config().packing === 'packed' ) $mol_wire_sync( record ).initial_write( fields, object, this.metadata( index ) )
			else {
				$mol_wire_sync( record ).Dataset( null )!.remote( this.dataset() )
				for( const field of fields ) {
					if( !Object.prototype.hasOwnProperty.call( object, field.title() ) ) throw new Error( `Missing fixture value: ${ field.title() }; config=${ JSON.stringify( this.config() ) }; values=${ Object.keys( object ).join(',') }` )
					$mol_wire_sync( record ).value_write_raw( field, object[field.title()], true )
				}
			}
			this.completed.add( index )
			return record
		}

		private readonly published = new Set< string >()

		/** Physical writers must be saved before resolving fresh canonical Land hosts. */
		write_lands() {
			return [...new Set([
				...this.prepared_lands.values(),
				...[...this.prepared_records.values(), ...this.prepared_tag_records.values()].map(record=>record.land()),
			])]
		}

		@ $mol_mem
		signature() { return new this.$.$shm_hitalama_benchmarks_goods_signature }

		@ $mol_mem_key
		initialized( count: number ) {
			this.initialized_range([ 0, count ])
			return Array.from({ length: count }, ( _, index )=> this.record( index ) )
		}

		@ $mol_mem_key
		initialized_range( range: readonly [ number, number ] ): boolean {
			const [ from, until ] = range
			if( until - from <= 32 ) this.batch( range )
			else {
				const middle = Math.floor( ( from + until ) / 2 )
				this.initialized_range([ from, middle ])
				// Same publication strategy as Goods: let wire reap completed reads
				// before more writes invalidate the shard's structural channels.
				this.$.$mol_wait_rest()
				this.initialized_range([ middle, until ])
			}
			return true
		}

		@ $mol_action
		append_all( count: number ) {
			const records = this.initialized( count )
			const key = `all:${ count }`
			if( !this.published.has( key ) ) {
				$mol_wire_sync( this.dataset() ).record_index_append( records )
				this.dataset().Record_sequence( null )!.val( count )
				this.published.add( key )
			}
			return records.map( record=> record.link().str )
		}

		@ $mol_mem_key
		snapshot_groups( count: number ) {
			const groups = new Map< $giper_baza_land, $shm_hitalama_map_widget_entity_table2_record[] >()
			for( let index = 0; index < count; ++index ) {
				const record = this.record( index )
				const land = record.land()
				const group = groups.get( land ) ?? []
				group.push( record )
				groups.set( land, group )
			}
			return [ ...groups.entries() ]
		}

		@ $mol_action
		snapshots_write( count: number ) {
			const fields = this.dataset().fields()
			for( const [ land, records ] of this.snapshot_groups( count ) ) {
				if( ! $mol_wire_sync( this.dataset().query_snapshot( land ) ).write( records, fields ) ) throw new Error( 'Query snapshot unsupported for fixture' )
			}
			return this.snapshot_groups( count ).length
		}

		@ $mol_mem_key
		batch( range: readonly [ number, number ] ) {
			return Array.from( { length: range[1] - range[0] }, ( _, offset )=> $mol_wire_sync( this ).initialize_record( range[0] + offset ) )
		}

		@ $mol_action
		append( range: readonly [ number, number ] ) {
			const records = this.batch( range )
			const key = range.join( ':' )
			if( !this.published.has( key ) ) {
				$mol_wire_sync( this.dataset() ).record_index_append( records )
				this.dataset().Record_sequence( null )!.val( range[1] )
				this.published.add( key )
			}
			return records.map( record=>record.link().str )
		}

		static product_name( index: number ) {
			const brands = [ 'Aurora', 'Atlas', 'Nova', 'Orion', 'Vega', 'Zen' ]
			const kinds = [ 'Notebook', 'Headphones', 'Monitor', 'Mouse', 'Keyboard', 'Camera', 'Printer', 'Router', 'Watch', 'Speaker' ]
			return `${ brands[Math.floor(index / 10) % 6] } ${ ['Wireless', 'Travel', 'Studio'][Math.floor(index / 60) % 3] } ${ kinds[index % 10] } ${ String(index).padStart(5, '0') }`
		}

	}
}
